export type EngagementSource = "cyclone" | "wordpress";
export type EngagementTokenPrefix = "CY" | "WP";

export interface EngagementIdentity {
  token: string;
  source: EngagementSource;
  sourceId: string;
}

export type EngagementTokenErrorCode =
  | "empty"
  | "malformed_token"
  | "non_canonical_order"
  | "too_many_tokens"
  | "unknown_prefix";

export interface EngagementTokenParseError {
  code: EngagementTokenErrorCode;
  message: string;
  token?: string;
  canonicalTokens?: string[];
}

export type EngagementTokenParseResult =
  | {
      ok: true;
      tokens: EngagementIdentity[];
      canonicalTokens: string[];
    }
  | {
      ok: false;
      error: EngagementTokenParseError;
    };

const MAX_ENGAGEMENT_TOKENS = 100;
const PAYLOAD_PATTERN = /^[^\s:]+$/;

export function canonicalizeEngagementTokens(tokens: string[]): string[] {
  return Array.from(new Set(tokens)).sort();
}

export function toEngagementIdentity(token: string): EngagementIdentity | null {
  const [prefix, sourceId] = splitToken(token);

  if (!prefix || !sourceId) {
    return null;
  }

  if (prefix === "CY") {
    return { token, source: "cyclone", sourceId };
  }

  if (prefix === "WP") {
    return { token, source: "wordpress", sourceId };
  }

  return null;
}

export function parseEngagementTokensParam(
  value: string | string[] | undefined,
): EngagementTokenParseResult {
  const rawTokens = normalizeTokenParam(value);

  if (rawTokens.length === 0) {
    return {
      ok: false,
      error: {
        code: "empty",
        message: "At least one engagement token is required.",
      },
    };
  }

  for (const token of rawTokens) {
    const [prefix, sourceId] = splitToken(token);

    if (prefix !== "CY" && prefix !== "WP") {
      return {
        ok: false,
        error: {
          code: "unknown_prefix",
          message: "Engagement token prefix must be CY or WP.",
          token,
        },
      };
    }

    if (!sourceId || !PAYLOAD_PATTERN.test(sourceId)) {
      return {
        ok: false,
        error: {
          code: "malformed_token",
          message: "Engagement token is malformed.",
          token,
        },
      };
    }
  }

  const canonicalTokens = canonicalizeEngagementTokens(rawTokens);

  if (canonicalTokens.length > MAX_ENGAGEMENT_TOKENS) {
    return {
      ok: false,
      error: {
        code: "too_many_tokens",
        message: "At most 100 engagement tokens are allowed.",
      },
    };
  }

  const uniqueInputOrder = Array.from(new Set(rawTokens));
  if (!arraysEqual(uniqueInputOrder, canonicalTokens)) {
    return {
      ok: false,
      error: {
        code: "non_canonical_order",
        message:
          "Engagement tokens must be de-duplicated and sorted alphabetically.",
        canonicalTokens,
      },
    };
  }

  return {
    ok: true,
    tokens: canonicalTokens.map((token) => toEngagementIdentity(token)!),
    canonicalTokens,
  };
}

function normalizeTokenParam(value: string | string[] | undefined): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.filter(
    (token): token is string => typeof token === "string" && token.length > 0,
  );
}

function splitToken(token: string): [string | null, string | null] {
  const separatorIndex = token.indexOf(":");

  if (separatorIndex === -1) {
    return [null, null];
  }

  return [
    token.slice(0, separatorIndex),
    token.slice(separatorIndex + 1) || null,
  ];
}

function arraysEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}
